import * as React from 'react';
import { X } from 'lucide-react';

import { cn } from '../lib/utils';

// Slide durations + curve. The curve is the iOS sheet easing (via Ionic; the
// same one vaul uses) — fast launch, long gentle settle. Open is slower than
// close (Material motion: enter decelerates, exit gets out of the way) and
// close is kept short so the panel doesn't appear to linger after the user
// clicks close — consumers typically clear the content on close (deviceId /
// id prop → null), so dragging out the slide-out leaves an empty rectangle.
// Match the `data-[state=open]:duration-300` / `data-[state=closed]:duration-200`
// Tailwind classes below; keep them in sync.
const ANIMATION_IN_MS = 300;
const ANIMATION_OUT_MS = 200;
// will-change is applied ONLY while a slide is plausibly running, then
// dropped: kept permanently it pins the panel on its own compositing layer,
// which blurs text at fractional devicePixelRatio (125%/150% display
// scaling). Slightly past the longest slide so the hint outlives the
// transition without lingering at rest.
const WILL_CHANGE_CLEAR_MS = Math.max(ANIMATION_IN_MS, ANIMATION_OUT_MS) + 50;
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

type Side = 'right';

interface InShellPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side?: Side;
  className?: string;
  overlayClassName?: string;
  hideCloseButton?: boolean;
  closeLabel?: string;
  children?: React.ReactNode;
  'data-testid'?: string;
  /**
   * If false, ESC and outside-click do not close the panel.
   * Default: true.
   */
  dismissable?: boolean;
}

/**
 * In-shell overlay panel — anchored inside the nearest positioned ancestor
 * (typically `#page-wrapper`, which AppShell renders with `position: relative;
 * overflow: hidden`). Use INSTEAD of `Sheet` when the panel should sit below
 * the global top navigation rather than over it.
 *
 * Why this exists (vs `Sheet`):
 *  - `Sheet` portals to `document.body` and renders at body z-50, so any
 *    nested dialog / dropdown / tooltip has to manually elevate above z-50
 *    via z-[60]/z-[70]/z-[80] hacks.
 *  - `InShellPanel` renders inline with `position: absolute` inside its
 *    nearest positioned ancestor and uses tiny *local* z-indexes (z-10 scrim,
 *    z-20 content). Anything portaled to `document.body` (the Radix default
 *    for `Dialog`, `AlertDialog`, `Popover`, `DropdownMenu`, `Tooltip`)
 *    naturally outranks it via DOM order — no manual z-index work at child
 *    call sites.
 *
 * Animation classes are copied verbatim from `Sheet` so motion is identical.
 *
 * NOT a drop-in replacement for `Sheet` in arbitrary contexts. Two intentional
 * choices in the codebase:
 *  - `Sheet`         — body-portaled, full-viewport, used when the panel
 *                      should overlay the navbar too.
 *  - `InShellPanel`  — page-scoped, sits below the navbar, used when the
 *                      panel should overlay page content but leave the
 *                      navbar interactive.
 */
const InShellPanel = React.forwardRef<HTMLDivElement, InShellPanelProps>(
  (
    {
      open,
      onOpenChange,
      side = 'right',
      className,
      overlayClassName,
      hideCloseButton,
      closeLabel = 'Close',
      children,
      dismissable = true,
      ...props
    },
    forwardedRef,
  ) => {
    const contentRef = React.useRef<HTMLDivElement>(null);
    React.useImperativeHandle(forwardedRef, () => contentRef.current as HTMLDivElement);

    const lastFocusedRef = React.useRef<HTMLElement | null>(null);
    const [shouldRender, setShouldRender] = React.useState(open);
    // `entered` lags `shouldRender` by one paint when opening so the very
    // first render is at `translate-x-full` and the next render flips to
    // `translate-x-0` — CSS transitions then slide the panel in. On close,
    // `entered` flips false immediately and the transition runs in reverse.
    const [entered, setEntered] = React.useState(false);
    // True only around a slide — gates the `will-change-transform` hint so the
    // compositor layer exists for the animation but is dropped at rest (a
    // permanent layer blurs text at fractional display scaling, e.g. 125%).
    const [animating, setAnimating] = React.useState(false);

    React.useEffect(() => {
      setAnimating(true);
      const t = window.setTimeout(() => setAnimating(false), WILL_CHANGE_CLEAR_MS);
      return () => window.clearTimeout(t);
    }, [open]);

    React.useEffect(() => {
      if (open) {
        setShouldRender(true);
        // Two rAFs: first commits the initial paint (entered=false →
        // translate-x-full), second flips `entered` → CSS transition fires.
        // A single rAF can fire before commit in React 19's batching, which
        // would skip the entry animation entirely.
        let raf2 = 0;
        const raf1 = window.requestAnimationFrame(() => {
          raf2 = window.requestAnimationFrame(() => setEntered(true));
        });
        return () => {
          window.cancelAnimationFrame(raf1);
          if (raf2) window.cancelAnimationFrame(raf2);
        };
      }
      setEntered(false);
      const t = window.setTimeout(() => setShouldRender(false), ANIMATION_OUT_MS);
      return () => window.clearTimeout(t);
    }, [open]);

    React.useEffect(() => {
      if (!open) return;
      lastFocusedRef.current = document.activeElement as HTMLElement | null;
      const focusFirst = () => {
        const el = contentRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        // `preventScroll` stops the browser from scrolling the nearest
        // scrollable ancestor to bring the (off-screen, mid-slide) close
        // button into view — that scroll is a visible page jump on open.
        el?.focus({ preventScroll: true });
      };
      const raf = window.requestAnimationFrame(focusFirst);
      return () => {
        window.cancelAnimationFrame(raf);
        lastFocusedRef.current?.focus();
      };
    }, [open]);

    React.useEffect(() => {
      if (!open) return;
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          if (!dismissable) return;
          e.preventDefault();
          onOpenChange(false);
          return;
        }
        if (e.key === 'Tab' && contentRef.current) {
          const focusables = Array.from(
            contentRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
          ).filter((el) => el.offsetParent !== null);
          if (focusables.length === 0) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          const active = document.activeElement as HTMLElement | null;
          if (e.shiftKey && active === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, dismissable, onOpenChange]);

    if (!shouldRender) return null;

    // Drive `data-state` from the animation-aware flag, not the raw `open` prop.
    // On mount-with-open, this renders 'closed' for the first paint (panel
    // off-screen) then 'open' on the next frame so the CSS transition slides
    // the panel in. On close, `entered` is set false before the unmount
    // timeout so the transition runs out.
    const state = entered ? 'open' : 'closed';

    return (
      <>
        {/* Overlay — fades out on close via plain transition-opacity. The
            project doesn't install tailwindcss-animate / tw-animate-css, so
            the `animate-in` / `fade-out-0` utilities used elsewhere are
            no-ops here. CSS transitions on opacity + transform work without
            any plugin. */}
        <div
          aria-hidden="true"
          data-state={state}
          onClick={() => {
            if (dismissable) onOpenChange(false);
          }}
          className={cn(
            'absolute inset-0 z-10 bg-dialog-overlay/50',
            'transition-opacity ease-out data-[state=open]:duration-300 data-[state=closed]:duration-200 motion-reduce:transition-none',
            'data-[state=closed]:opacity-0',
            overlayClassName,
          )}
        />
        <div
          ref={contentRef}
          role="dialog"
          aria-modal="true"
          data-state={state}
          data-testid={props['data-testid']}
          className={cn(
            'absolute z-20 gap-4 bg-background p-6 shadow-sm',
            // `will-change-transform` ONLY while a slide runs (see `animating`):
            // the heavy panel content makes the browser pre-promote a compositor
            // layer so the slide doesn't get eaten by first-paint jank — but kept
            // permanently it blurs text at fractional devicePixelRatio (125%/150%
            // display scaling), so it's dropped at rest.
            animating && 'will-change-transform',
            // Transition `translate` (Tailwind v4 translate-x-* uses the
            // standalone `translate` property) AND `width`/`max-width` (drawer
            // enlarge toggle — max-width must animate too, else collapsing
            // snaps: the smaller max-w clamps the still-animating width on the
            // first frame) so consumers don't need their own conflicting
            // `transition-*` class — twMerge would override, killing the slide.
            // Curve = iOS sheet easing (vaul's curve); 300ms in / 200ms out —
            // keep in sync with ANIMATION_IN_MS / ANIMATION_OUT_MS.
            'transition-[translate,width,max-width] ease-[cubic-bezier(0.32,0.72,0,1)]',
            'data-[state=open]:duration-300 data-[state=closed]:duration-200',
            'motion-reduce:transition-none',
            side === 'right' &&
              cn(
                'inset-y-0 right-0 w-full border-l sm:w-[40vw] sm:max-w-[40vw] overflow-y-auto',
                // Closed state slides off-screen to the right. Open state
                // sits at translate-x-0 (default).
                'data-[state=closed]:translate-x-full',
              ),
            className,
          )}
        >
          {!hideCloseButton && (
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label={closeLabel}
              className="absolute right-4 top-4 cursor-pointer rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">{closeLabel}</span>
            </button>
          )}
          {children}
        </div>
      </>
    );
  },
);
InShellPanel.displayName = 'InShellPanel';

const InShellPanelHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
);
InShellPanelHeader.displayName = 'InShellPanelHeader';

const InShellPanelFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
);
InShellPanelFooter.displayName = 'InShellPanelFooter';

const InShellPanelTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h2 ref={ref} className={cn('text-lg font-semibold text-foreground', className)} {...props} />
));
InShellPanelTitle.displayName = 'InShellPanelTitle';

const InShellPanelDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
));
InShellPanelDescription.displayName = 'InShellPanelDescription';

export {
  InShellPanel,
  InShellPanelHeader,
  InShellPanelFooter,
  InShellPanelTitle,
  InShellPanelDescription,
};
export type { InShellPanelProps };
